import { createContext, useContext, useMemo, type ReactNode } from "react";

import { dashKey, useDashboardPerfs, EMPTY_PERFS, type DashboardPerfs } from "./useDashboardPerfs";

interface DashboardPerfsContextValue {
  /** The dashboard these perfs were read for, as a comparable key. Consumers
   *  compare their own scope against it and fall back to their own request when
   *  the two don't match, so a table can never show another dashboard's views. */
  dashboardKey: string | null;
  perfs: DashboardPerfs;
}

const Ctx = createContext<DashboardPerfsContextValue | null>(null);

/**
 * Fires the dashboard's ONE `dashboard/custom-views` call and shares the result
 * with everything underneath: the page reads the saved filters off it before it
 * requests any metrics, and the By-Branch / By-Clinician tables read their saved
 * column layouts off the same response instead of re-requesting it once they
 * mount.
 *
 * `dashboardID` null keeps the call idle — the id only exists once
 * `dashboard/config` has resolved.
 */
export function DashboardPerfsProvider({
  dashboardID,
  children,
}: {
  dashboardID: number | string | null;
  children: ReactNode;
}) {
  const perfs = useDashboardPerfs(dashboardID != null ? { dashboardID } : null);
  const value = useMemo<DashboardPerfsContextValue>(
    () => ({ dashboardKey: dashKey(dashboardID), perfs }),
    [dashboardID, perfs],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The dashboard's shared perfs. Outside a provider (All Metrics, which isn't
 *  tied to a dashboard) this returns the idle value, and callers fetch for
 *  themselves. */
export function useDashboardPerfsContext(): DashboardPerfs {
  return useContext(Ctx)?.perfs ?? EMPTY_PERFS;
}

/**
 * The shared perfs when they cover `dashboardID`, else null — the signal to a
 * table that it has to read its own. A provider that hasn't resolved a dashboard
 * id yet (`dashboardKey === null`) serves nobody: All Metrics' own views live
 * under exactly that scope and must not be answered from a dashboard's response.
 */
export function useScopedDashboardPerfs(
  dashboardID: number | string | null | undefined,
): DashboardPerfs | null {
  const ctx = useContext(Ctx);
  if (!ctx || ctx.dashboardKey === null) return null;
  return ctx.dashboardKey === dashKey(dashboardID) ? ctx.perfs : null;
}
